/**
 * THE THEME SWITCH (docs/plans/DARK-MODE.md §4, §6). One attribute decides
 * the theme: `<html data-theme>` is `wireframe` (light, the default) or
 * `giraffe-dark`. DaisyUI's classes and the design-system `--ee-*` tokens
 * both hang off it, so they flip together.
 *
 * The same resolution runs twice: once in the inline boot script in
 * `app/index.html` (before any stylesheet, so the first painted pixel is the
 * right colour) and then here, for the store. The boot script is a minified
 * copy of `resolveTheme` + `applyTheme`; `utils/__tests__/theme-boot.test.ts`
 * runs both over one table of inputs and fails when they disagree.
 *
 * Resolution: the stored choice (`localStorage['ee.theme']`): `dark` → dark,
 * `device` → the OS scheme, anything else (missing, invalid, storage
 * throwing) → light. An OS-dark device with no choice opens LIGHT. Dark is
 * offered everywhere, web and native: the D5 launch flag and its review-build
 * `?theme=` escape were removed on 2026-10-28 (David). The games site still
 * reads `?theme=`: that is how the app hands its theme over
 * (utils/storyforge.ts comicUrl), not an escape.
 *
 * Every path follows the choice. v1 pinned the public pages (the landing,
 * /learn, /quiz-assessment) light by path (D6); B8 converted them and lifted
 * the pin, so the choice made on the landing's sun/moon toggle (D9) is the
 * one the app opens with. A crawler stores nothing, so it is served light.
 */

export type ThemePreference = 'light' | 'dark' | 'device';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ee.theme';

/** The `<html data-theme>` value of each theme (DaisyUI theme names). */
export const DATA_THEME: Record<ResolvedTheme, string> = {
  light: 'wireframe',
  dark: 'giraffe-dark',
};

/**
 * The first painted colour of each theme (`--ee-body`), which is also the
 * `<meta name="theme-color">` (the browser's UI colour) and the native status
 * bar. Light is today's white body, unchanged (D10).
 */
export const BODY_COLOUR: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#1b1510',
};

/** The Android navigation bar in dark (`--ee-nav-surface`). */
export const NAV_SURFACE_DARK = '#211912';

/** A stored value as a preference: anything unknown is light. */
export function parsePreference(value: unknown): ThemePreference {
  return value === 'dark' || value === 'device' ? value : 'light';
}

export interface ThemeInputs {
  /** `localStorage['ee.theme']`, or null when missing or unreadable. */
  stored: string | null;
  /** `matchMedia('(prefers-color-scheme: dark)').matches`. */
  prefersDark: boolean;
}

export function resolveTheme({ stored, prefersDark }: ThemeInputs): ResolvedTheme {
  const preference = parsePreference(stored);
  if (preference === 'dark') return 'dark';
  if (preference === 'device') return prefersDark ? 'dark' : 'light';
  return 'light';
}

/** `localStorage['ee.theme']`, or null when missing or storage throws. */
export function readStoredTheme(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist a choice; a blocked storage keeps it for this page only. */
export function writeStoredTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // private window / blocked storage: the choice lasts until reload
  }
}

export const DARK_QUERY = '(prefers-color-scheme: dark)';

export function prefersDark(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * The three writes the boot script makes, idempotent (overwrite, never
 * append): `data-theme`, `color-scheme`, and the one theme-color meta.
 */
export function applyTheme(resolved: ResolvedTheme, doc: Document = document): void {
  const root = doc.documentElement;
  root.dataset.theme = DATA_THEME[resolved];
  root.style.colorScheme = resolved;
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', BODY_COLOUR[resolved]);
}

/** The theme the document currently shows (what the boot script decided). */
export function currentResolvedTheme(doc: Document = document): ResolvedTheme {
  return doc.documentElement.dataset.theme === DATA_THEME.dark ? 'dark' : 'light';
}
